/**
 * Container Component
 * 
 * Flexible layout/grouping component that can act as:
 * - Layout orchestrator (with layout, gap, columns, padding properties)
 * - Grouping unit (with label, border properties)
 * - Both (combining layout and grouping)
 * - Simple vertical stack (with minimal configuration)
 * 
 * Supports recursive nesting and responsive behavior via Tailwind CSS.
 */

import React from 'react';
import { Badge, Card } from '@strivelabs/ui-core';
import { RichTextRenderer } from '../rich-text';

// Layout types supported
export type ContainerLayout = 'masonry' | 'grid' | 'flex';
export type ContainerPadding = 'none' | 'sm' | 'md' | 'lg';
export type ContainerScroll = 'auto' | 'always' | 'none';
export type ContainerBorderColor = 'red' | 'green' | 'yellow' | 'gray' | 'blue' | 'purple';

export interface ContainerProps {
  // Layout properties
  layout?: ContainerLayout;
  // 'masonry': Newspaper-style vertical columns (content flows top-to-bottom within each column)
  // 'grid': Fixed grid rows (content flows left-to-right, then wraps to new row)
  // 'flex': Responsive wrapping (items wrap to next row when space runs out, like tag clouds)
  gap?: number;
  columns?: 1 | 2 | 3 | 4; // fixed columns when provided; otherwise responsive free-flow
  padding?: ContainerPadding;
  maxWidth?: number | string;
  height?: number | string;
  scroll?: ContainerScroll;

  // Grouping properties
  id?: string;
  label?: string;
  border?: boolean;
  borderColor?: ContainerBorderColor; // colored left border (e.g., for insights cards)
  width?: number | string;

  // Header properties (optional heading with badge)
  heading?: string; // supports markdown
  headingBadge?: string;
  description?: string; // optional description text below heading

  // Content
  children: React.ReactNode;
  className?: string;
}

const getPaddingClass = (padding: ContainerPadding = 'md'): string => {
  const paddingMap: Record<ContainerPadding, string> = {
    'none': 'p-0',
    'sm': 'p-2',
    'md': 'p-4',
    'lg': 'p-6',
  };
  return paddingMap[padding];
};

const GAP_UNIT_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 10, 12, 14, 16] as const;

const CardComponent = Card as React.ComponentType<React.PropsWithChildren<{ className?: string }>>;

const getClosestGapUnits = (gap?: number): number => {
  const gapValue = gap ?? 16; // default 16px
  const units = Math.round(gapValue / 4); // Tailwind scale (4px per unit)
  return GAP_UNIT_OPTIONS.reduce((prev, curr) =>
    Math.abs(curr - units) < Math.abs(prev - units) ? curr : prev
  );
};

const getGapClassName = (gap?: number): string => {
  const units = getClosestGapUnits(gap);
  return `gap-${units}`;
};

// Map numeric pixel gap to Tailwind column gap and item margin-bottom classes
const getMasonryGapClasses = (gap?: number): { containerGapClass: string; itemMarginClass: string } => {
  const units = getClosestGapUnits(gap);
  return {
    containerGapClass: `gap-x-${units}`,
    itemMarginClass: `mb-${units}`,
  };
};

const getMasonryColumnsClass = (columns?: 1 | 2 | 3 | 4): string | undefined => {
  if (!columns) return undefined;
  const columnMap: Record<1 | 2 | 3 | 4, string> = {
    1: 'columns-1',
    2: 'columns-2',
    3: 'columns-3',
    4: 'columns-4',
  };
  return columnMap[columns];
};

const getGridColumnsClass = (columns?: 1 | 2 | 3 | 4): string => {
  if (!columns) return 'grid-cols-1'; // default to 1 column if not specified
  const columnMap: Record<1 | 2 | 3 | 4, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
  };
  return columnMap[columns];
};

const getBorderColorClass = (borderColor?: ContainerBorderColor): string => {
  if (!borderColor) return '';
  const borderColorMap: Record<ContainerBorderColor, string> = {
    red: 'border-l-4 border-l-red-600',
    green: 'border-l-4 border-l-green-600',
    yellow: 'border-l-4 border-l-yellow-600',
    gray: 'border-l-4 border-l-gray-600',
    blue: 'border-l-4 border-l-blue-600',
    purple: 'border-l-4 border-l-purple-600',
  };
  return borderColorMap[borderColor];
};

const getScrollClass = (scroll: ContainerScroll = 'auto'): string => {
  const scrollMap: Record<ContainerScroll, string> = {
    'auto': 'overflow-auto',
    'always': 'overflow-scroll',
    'none': 'overflow-hidden',
  };
  return scrollMap[scroll];
};

const getSizeValue = (value?: number | string): string | undefined => {
  if (value === undefined) return undefined;
  if (typeof value === 'number') return `${value}px`;
  return value;
};

export const Container: React.FC<ContainerProps> = ({
  layout = 'masonry',
  gap,
  columns,
  padding = 'none',
  maxWidth,
  height,
  scroll = 'auto',
  id,
  label,
  border = false,
  borderColor,
  width,
  heading,
  headingBadge,
  description,
  children,
  className,
}) => {
  const styles: React.CSSProperties = {};

  if (maxWidth) styles.maxWidth = getSizeValue(maxWidth);
  if (height) styles.height = getSizeValue(height);
  if (width) styles.width = getSizeValue(width);

  let content: React.ReactNode;

  if (layout === 'grid') {
    // Grid layout - horizontal alignment
    const gridClasses = [
      'grid',
      getGridColumnsClass(columns),
      gap !== undefined ? getGapClassName(gap) : 'gap-8',
      getPaddingClass(padding),
      getScrollClass(scroll),
    ];

    if (border) {
      gridClasses.push('border rounded-lg');
    }

    // Note: borderColor is applied to Card wrapper, not here
    const finalClassName = [gridClasses.join(' '), className].filter(Boolean).join(' ');

    content = (
      <div className={finalClassName} id={id} style={styles}>
        {React.Children.toArray(children)}
      </div>
    );
  } else if (layout === 'flex') {
    // Flex layout - responsive wrapping
    const gapValue = gap ?? 16; // default 16px
    const gapClass = getGapClassName(gap);

    const flexClasses = [
      'flex',
      'flex-wrap',
      gapClass,
      getPaddingClass(padding),
      getScrollClass(scroll),
    ];

    if (border) {
      flexClasses.push('border rounded-lg');
    }

    // Note: borderColor is applied to Card wrapper, not here
    const finalClassName = [flexClasses.join(' '), className].filter(Boolean).join(' ');

    // Calculate flex-basis based on columns (if provided)
    const flexBasis = columns ? `calc(${100 / columns}% - ${gapValue}px)` : 'auto';

    content = (
      <div className={finalClassName} id={id} style={styles}>
        {React.Children.toArray(children).map((child, idx) => (
          <div key={idx} style={{ flexBasis, minWidth: 0 }}>
            {child}
          </div>
        ))}
      </div>
    );
  } else {
    // Masonry layout - vertical stacking with columns
    const { containerGapClass, itemMarginClass } = getMasonryGapClasses(gap);

    const masonryClasses = [
      getPaddingClass(padding),
      getScrollClass(scroll),
      getMasonryColumnsClass(columns) ?? 'columns-[16rem]', // responsive free-flow by default
      containerGapClass,
    ];

    if (border) {
      masonryClasses.push('border rounded-lg');
    }

    // Note: borderColor is applied to Card wrapper, not here
    const finalClassName = [masonryClasses.join(' '), className].filter(Boolean).join(' ');

    content = (
      <div className={finalClassName} id={id} style={styles}>
        {React.Children.toArray(children).map((child, idx) => (
          <div key={idx} className={`break-inside-avoid ${itemMarginClass}`}>
            {child}
          </div>
        ))}
      </div>
    );
  }

  // Helper function to render the container with optional heading
  const renderContainer = (innerContent: React.ReactNode) => {
    // If label is provided, render semantic label
    if (label) {
      return (
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">{label}</label>
          {innerContent}
        </div>
      );
    }

    return innerContent;
  };

  // If borderColor is specified, wrap in Card component for card-like appearance
  // and include heading inside the Card so border wraps everything
  const borderColorClass = getBorderColorClass(borderColor);
  const wrappedContent = borderColor ? (
    <CardComponent className={`w-full h-full p-4 md:p-6 ${borderColorClass}`}>
      {heading && (
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-2">
            <RichTextRenderer
              content={heading}
              className="[&>p]:text-[28px] [&>p]:leading-tight [&>p]:font-medium text-custom-text-secondary"
            />
            {headingBadge && (
              <Badge variant="customGray" className="flex-shrink-0">
                {headingBadge}
              </Badge>
            )}
          </div>
          {description && (
            <p className="text-sm text-custom-text-secondary mt-2">{description}</p>
          )}
        </div>
      )}
      {renderContainer(content)}
  </CardComponent>
  ) : (
    // For non-bordered containers, render heading outside
    <>
      {heading && (
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-2">
            <RichTextRenderer
              content={heading}
              className="[&>p]:text-2xl [&>p]:font-semibold text-custom-text-primary"
            />
            {headingBadge && (
              <Badge variant="customGray" className="flex-shrink-0">
                {headingBadge}
              </Badge>
            )}
          </div>
          {description && (
            <p className="text-sm text-custom-text-secondary mt-2">{description}</p>
          )}
        </div>
      )}
      {renderContainer(content)}
    </>
  );

  return wrappedContent;
};

export default Container;